import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from 'react-dom';
import { FaTimes, FaDownload, FaEdit, FaTrash, FaExpand, FaPlay, FaPause, FaShare } from "react-icons/fa";
import ReactPlayer from 'react-player';
// import ReelContent from '../pages/ReelContent'; // Commented out as not used

const GeneratedVideo = ({
  isGeneratingContent,
  contentGenerated,
  generatedImgContent,
  generatedVidContent,
  instagramPostType,
  timeRemaining,
  selectedImages,
  isEditingContent,
  editedContent,
  setEditedContent,
  isPlaying,
  setIsPlaying,
  isPublishing,
  publishStatus,
  contentTimestamp,
  viewerIsOpen,
  currentLightboxImage,
  creditInfo,
  user,
  handleStopGeneration,
  handleToggleEdit,
  handlePublish,
  handleScheduleLater,
  openLightbox,
  closeLightbox,
  handleClearContent,
  // New props for session-wide carousel
  generatedImgHistory = [],
  sessionSelectedImageIndex,
  setSessionSelectedImageIndex
}) => {
  // const [isTextExpanded, setIsTextExpanded] = useState(false); // Commented out as not used for video
  const [containerHeight, setContainerHeight] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const playerRef = useRef(null);

  // Calculate dynamic height based on viewport
  useEffect(() => {
    const calculateHeight = () => {
      const headerHeight = 64; // Approximate header height
      const chatboxHeight = window.innerWidth < 1024 ? 0 : 400; // Chatbox height on desktop
      const padding = 32; // Top and bottom padding
      const availableHeight = window.innerHeight - headerHeight - chatboxHeight - padding;
      setContainerHeight(Math.max(400, availableHeight)); // Minimum 400px
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  // Function to truncate text to first line (commented out as not used for video)
  // const truncateText = (text) => {
  //   if (!text) return '';
  //   const firstLine = text.split('\n')[0];
  //   return firstLine.length > 100 ? firstLine.substring(0, 100) + '...' : firstLine;
  // };

  // Function to check if text needs truncation (commented out as not used for video)
  // const needsTruncation = (text) => {
  //   if (!text) return false;
  //   return text.includes('\n') || text.length > 100;
  // };

  // Flatten history into a single array of videos with content linkage
  const flattenedVideos = Array.isArray(generatedImgHistory)
    ? generatedImgHistory.flatMap((entry) =>
        (entry.images || [])
          .filter(img => img.isVideo) // Only include videos
          .map((video) => ({
            ...video,
            __entryContent: entry.content || '',
            __entryTimestamp: entry.timestamp || ''
          }))
      )
    : [];

  // Fallback for older state (single batch) if no history provided
  const singleBatch = Array.isArray(flattenedVideos) && flattenedVideos.length === 0 && generatedVidContent && Array.isArray(generatedVidContent.images)
    ? generatedVidContent.images
        .filter(img => img.isVideo) // Only include videos
        .map((video) => ({ 
          ...video, 
          __entryContent: generatedVidContent.content || '', 
          __entryTimestamp: contentTimestamp || '' 
        }))
    : [];

  const allVideos = flattenedVideos.length > 0 ? flattenedVideos : singleBatch;
  const totalVideos = allVideos.length;

  const selectedIndex = typeof sessionSelectedImageIndex === 'number' && sessionSelectedImageIndex >= 0 && sessionSelectedImageIndex < totalVideos
    ? sessionSelectedImageIndex
    : (totalVideos > 0 ? totalVideos - 1 : 0);

  const selectedItem = totalVideos > 0 ? allVideos[selectedIndex] : null;
  const selectedContentText = selectedItem ? (selectedItem.__entryContent || '') : (generatedVidContent?.content || '');

  const handlePrev = () => {
    if (totalVideos === 0) return;
    const prevIdx = (selectedIndex - 1 + totalVideos) % totalVideos;
    setSessionSelectedImageIndex(prevIdx);
    setIsVideoPlaying(false); // Stop video when navigating
  };

  const handleNext = () => {
    if (totalVideos === 0) return;
    const nextIdx = (selectedIndex + 1) % totalVideos;
    setSessionSelectedImageIndex(nextIdx);
    setIsVideoPlaying(false); // Stop video when navigating
  };

  // Handle video play/pause toggle
  const handleVideoToggle = useCallback(() => {
    setIsVideoPlaying(prev => !prev);
  }, []);

  // Handle video ready event for optimization
  const handleVideoReady = useCallback(() => {
    // Video is ready to play
  }, []);

  // Handle video error
  const handleVideoError = useCallback((error) => {
    console.error('Video playback error:', error);
    setIsVideoPlaying(false);
  }, []);

  const handleDownload = async () => {
    if (!selectedItem || !selectedItem.url) return;
    
    const ext = selectedItem.isVideo ? 'mp4' : 'png';
    
    // Detect mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
      || (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
    
    // On mobile, use Web Share API to allow saving to gallery
    if (isMobile && navigator.share) {
      try {
        const response = await fetch(selectedItem.url, { mode: 'cors' });
        const blob = await response.blob();
        const file = new File([blob], `generated-video-${Date.now()}.${ext}`, { type: blob.type });
        
        const shareData = {
          title: 'Generated Video',
          text: 'Download your generated video',
          files: [file]
        };

        // Check if files can be shared
        if (navigator.canShare && navigator.canShare(shareData)) {
          await navigator.share(shareData);
          return;
        }
      } catch (err) {
        // If share fails or is cancelled, fall through to download
        if (err.name === 'AbortError') {
          return; // User cancelled, don't show error
        }
        console.error('Share failed, falling back to download:', err);
      }
    }
    
    // Desktop download or mobile fallback
    try {
      const response = await fetch(selectedItem.url, { mode: 'cors' });
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `generated-video-${Date.now()}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (_) {
      // Fallback to direct link if CORS blocks fetch
      const link = document.createElement('a');
      link.href = selectedItem.url;
      link.download = `generated-video-${Date.now()}.${ext}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleMaximize = (e) => {
    const event = e || { preventDefault: () => {} };
    if (selectedItem?.url) {
      openLightbox(selectedItem.url, selectedItem.isVideo ? 'video' : 'image');
    }
  };

  const handleShare = async () => {
    if (!selectedItem || !selectedItem.url) return;
    
    // Check if Web Share API is supported
    if (navigator.share) {
      try {
        // Fetch the video as a blob for native sharing
        const response = await fetch(selectedItem.url, { mode: 'cors' });
        const blob = await response.blob();
        const ext = selectedItem.isVideo ? 'mp4' : 'png';
        const file = new File([blob], `generated-video.${ext}`, { type: blob.type });
        
        const shareData = {
          title: 'Generated Video',
          text: selectedContentText ? selectedContentText.substring(0, 100) : 'Check out this cool AI video from Taco!',
          files: [file]
        };

        // Check if files can be shared
        if (navigator.canShare && navigator.canShare(shareData)) {
          await navigator.share(shareData);
        } else {
          // Fallback to sharing just the URL if files aren't supported
          await navigator.share({
            title: 'Generated Video',
            text: selectedContentText ? selectedContentText.substring(0, 100) : 'Check out this cool AI video from Taco!',
            url: selectedItem.url
          });
        }
      } catch (err) {
        // User cancelled or error occurred
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      console.log('Web Share API not supported');
    }
  };

  // Credit info display as overlay
  const getCreditText = () => {
    if (!creditInfo) return null;
    
    const { total_cost, time_taken_sec } = creditInfo;
    
    if (typeof total_cost === 'number' || typeof time_taken_sec === 'number') {
      const costText = typeof total_cost === 'number' ? `$${total_cost.toFixed(4)}` : null;
      const timeText = typeof time_taken_sec === 'number' ? `${time_taken_sec.toFixed(2)}s` : null;
      return [costText, timeText].filter(Boolean).join(' • ');
    }
    
    const tokensRemaining = creditInfo.credit_left_tokens || creditInfo.credit_left || creditInfo.tokens || null;
    if (tokensRemaining !== null && tokensRemaining !== undefined && tokensRemaining !== '') {
      return `💎 ${tokensRemaining} tokens remaining`;
    }
    
    return null;
  };

  return (
    <>
      {/* Generated Content Section */}
      {contentGenerated && allVideos.length > 0 && (
        <div 
          className="w-full h-full flex items-center justify-center lg:items-stretch lg:justify-stretch"
          style={{ height: `${containerHeight}px` }}
        >

            <div className="relative w-full h-full flex flex-col lg:flex-row gap-4 max-w-7xl lg:max-w-none mx-auto lg:mx-0 px-4 lg:px-0">
              
              {/* Main Video Display */}
              <div className="flex-1 relative bg-black/20 rounded-2xl overflow-hidden backdrop-blur-sm border border-gray-600/30">
                
                {/* Action Buttons Overlay */}
                <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
                  <button
                    onClick={handleShare}
                    className="w-10 h-10 bg-black/60 hover:bg-black/80 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm lg:hidden"
                    title="Share"
                  >
                    <FaShare className="text-sm" />
                  </button>
                  <button
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleMaximize(event);
                    }}
                    className="w-10 h-10 bg-black/60 hover:bg-black/80 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    title="Maximize"
                  >
                    <FaExpand className="text-sm" />
                  </button>
                  <button
                    onClick={handleDownload}
                    className="w-10 h-10 bg-black/60 hover:bg-black/80 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    title="Download"
                  >
                    <FaDownload className="text-sm" />
                  </button>
                  {/* <button
                    onClick={handleToggleEdit}
                    className="w-10 h-10 bg-black/60 hover:bg-black/80 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    title="Edit"
                  >
                    <FaEdit className="text-sm" />
                  </button> */}
                  {/* <button
                    onClick={handleClearContent}
                    className="w-10 h-10 bg-black/60 hover:bg-black/80 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    title="Clear"
                  >
                    <FaTrash className="text-sm" />
                  </button> */}
                </div>

                {/* Navigation Buttons */}
                {totalVideos > 1 && (
                  <>
                    <button
                      onClick={handlePrev}
                      className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 backdrop-blur-sm border border-gray-500/50"
                      title="Previous video"
                    >
                      ‹
                    </button>
                    <button
                      onClick={handleNext}
                      className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 backdrop-blur-sm border border-gray-500/50"
                      title="Next video"
                    >
                      ›
                    </button>
                  </>
                )}

                {/* Main Video Player */}
                <div className="w-full h-full flex items-center justify-center p-4 relative">
                  {selectedItem?.url && (
                    <>
                      <ReactPlayer
                        ref={playerRef}
                        url={selectedItem.url}
                        width="100%"
                        height="100%"
                        playing={isVideoPlaying}
                        controls={false}
                        playsinline={true}
                        loop={true}
                        muted={false}
                        onReady={handleVideoReady}
                        onError={handleVideoError}
                        style={{ 
                          objectFit: 'contain',
                          borderRadius: '8px'
                        }}
                        config={{
                          file: {
                            attributes: {
                              preload: 'metadata' // Optimize loading
                            }
                          }
                        }}
                        onClick={(event) => {
                          // Prevent video toggle when clicking on the player itself
                          event.stopPropagation();
                          handleMaximize(event);
                        }}
                      />
                      
                      {/* Custom Play/Pause Button Overlay */}
                      <div 
                        className="absolute inset-0 flex items-center justify-center cursor-pointer"
                        onClick={(event) => {
                          // Check if click is on the play button area
                          const rect = event.currentTarget.getBoundingClientRect();
                          const centerX = rect.left + rect.width / 2;
                          const centerY = rect.top + rect.height / 2;
                          const distance = Math.sqrt(
                            Math.pow(event.clientX - centerX, 2) + Math.pow(event.clientY - centerY, 2)
                          );
                          
                          // If click is near center (play button area), toggle video
                          if (distance < 50) {
                            handleVideoToggle();
                          } else {
                            // Otherwise, open lightbox
                            handleMaximize(event);
                          }
                        }}
                      >
                        <div 
                          className="w-16 h-16 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 backdrop-blur-sm border border-gray-500/50"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleVideoToggle();
                          }}
                        >
                          {isVideoPlaying ? (
                            <FaPause className="text-white text-xl" />
                          ) : (
                            <FaPlay className="text-white text-xl ml-1" />
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Credit Info Overlay (like filename display) */}
                {/* {getCreditText() && (
                  <div className="absolute bottom-4 left-4 bg-black/60 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm border border-gray-500/50">
                    {getCreditText()}
                  </div>
                )} */}

                {/* Thumbnail Strip Overlay - Bottom */}
                {totalVideos > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {allVideos.map((video, idx) => (
                      <button
                        key={`${video.url}-${idx}`}
                        className={`relative w-12 h-12 rounded-lg overflow-hidden border-2 transition-all duration-200 hover:scale-110 ${
                          idx === selectedIndex 
                            ? 'border-purple-400 ring-2 ring-purple-400/50' 
                            : 'border-gray-400/50 hover:border-gray-300'
                        }`}
                        onClick={() => {
                          setSessionSelectedImageIndex(idx);
                          setIsVideoPlaying(false); // Stop current video when switching
                        }}
                        title={video.prompt || `Video ${idx + 1}`}
                      >
                        {video.isVideo ? (
                          <div className="w-full h-full bg-black">
                            <ReactPlayer 
                              url={video.url} 
                              width="100%" 
                              height="100%" 
                              playing={false} 
                              controls={false} 
                              playsinline={true} 
                              style={{ objectFit: 'cover' }} 
                            />
                          </div>
                        ) : (
                          <img 
                            src={video.url} 
                            alt={video.prompt || `thumb-${idx}`} 
                            className="w-full h-full object-cover" 
                          />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Content Panel - Right side on desktop, bottom on mobile */}
              <div className="w-full lg:w-80 p-6 flex flex-col">
                
              </div>
            </div>
          
        </div>
      )}

      {/* Lightbox Modal - Rendered via Portal */}
      {viewerIsOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-90 p-4"
          style={{ zIndex: 999999 }}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            style={{ zIndex: 1000000 }}
          >
            <FaTimes className="text-3xl" />
          </button>
          <div
            className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedItem?.isVideo ? (
              <ReactPlayer
                url={selectedItem.url}
                width="90vw"
                height="90vh"
                playing={true}
                controls={true}
                playsinline={true}
                loop={true}
                muted={false}
                config={{
                  file: {
                    attributes: {
                      preload: 'auto',
                      controlsList: 'nodownload'
                    }
                  }
                }}
              />
            ) : (
              <img
                src={selectedItem?.url}
                alt={selectedItem?.prompt || 'Generated content'}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GeneratedVideo; 